import { FormEvent, useState } from 'react';
import { api, ApiError, setToken } from '../lib/api';
import { useNova } from '../lib/store';
import type { AuthUser } from '../lib/types';
import { landingView } from '../App';
import { ThemePicker } from './ThemePicker';
import { Button } from './ui';

export function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await api<{ token: string; user: AuthUser }>('POST', '/auth/login', { email, password });
      setToken(res.token);
      useNova.getState().set({ user: res.user, view: landingView(res.user.role) });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Sign-in failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="grid min-h-dvh place-items-center px-4">
      <div className="w-full max-w-[400px]">
        <div className="mb-10 flex items-center gap-3">
          <NovaMark size={40} />
          <div>
            <p className="text-2xl font-semibold tracking-tight">Nova</p>
            <p className="text-sm text-ink-3">Contact center</p>
          </div>
        </div>
        <form onSubmit={submit} className="glass rounded-3xl p-7">
          <h1 className="mb-6 text-lg font-medium">Sign in to start your shift</h1>
          <label className="mb-4 block">
            <span className="mb-1.5 block text-sm text-ink-2">Work email</span>
            <input
              type="email"
              autoComplete="username"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-xl border border-line bg-abyss/60 px-3.5 py-2.5 outline-none transition focus:border-ion"
            />
          </label>
          <label className="mb-6 block">
            <span className="mb-1.5 block text-sm text-ink-2">Password</span>
            <input
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-xl border border-line bg-abyss/60 px-3.5 py-2.5 outline-none transition focus:border-ion"
            />
          </label>
          {error ? (
            <p role="alert" className="mb-4 rounded-xl border border-break/40 bg-break/10 px-3 py-2 text-sm text-break">
              {error}
            </p>
          ) : null}
          <Button tone="primary" type="submit" className="w-full" disabled={busy}>
            {busy ? 'Signing in…' : 'Sign in'}
          </Button>
        </form>
        <p className="mt-6 text-sm leading-relaxed text-ink-3">
          Your browser will ask for the microphone once you are signed in. Calls ring right here, no desk phone needed.
        </p>
        <div className="mt-6 w-[260px]">
          <ThemePicker />
        </div>
      </div>
    </main>
  );
}

export function NovaMark({ size = 28 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 40 40" aria-hidden>
      <circle cx="20" cy="20" r="15" fill="none" stroke="var(--color-ion)" strokeWidth="2.5" opacity="0.35" />
      <path d="M20 5a15 15 0 0 1 15 15" fill="none" stroke="var(--color-ion)" strokeWidth="2.5" strokeLinecap="round" />
      <circle cx="20" cy="20" r="5.5" fill="var(--color-ion)" />
    </svg>
  );
}
